import React, { useState, useRef, useEffect } from 'react';
import { GoogleMap, LoadScript, Polyline, Marker, Circle, Polygon } from '@react-google-maps/api';
import Papa from 'papaparse';
import { storage, firestore } from '../../helpers/firebaseConfig'; // Your Firebase configuration
import { updateSimulationName } from '../../helpers/firebaseHelpers';
import { ref, getDownloadURL } from 'firebase/storage';
import BlockedRoadIcon from '../../assets/images/blockedroad_icon.png';
import ContraflowIcon from '../../assets/images/contraflow.png';
import { collection, getDocs } from 'firebase/firestore';
import { formatTime, getTimeStep, alignToStep } from '../../helpers/generalHelpers';
import ColourBar from "./ColourBar"
import SimulationSelector from './SimulationSelector';
import SimulationNameEditor from './SimulationNameEditor';


const containerStyle = {
  width: '100%',
  height: '740px',
  borderRadius: '0px',
  overflow: 'hidden',
  marginBottom: '20px'
};

const darkMapStyle = [
  { elementType: "geometry", stylers: [{ color: "#242f3e" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#242f3e" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#746855" }] },
  {
    featureType: "administrative.locality",
    elementType: "labels.text.fill",
    stylers: [{ color: "#d59563" }],
  },
  {
    featureType: "poi",
    elementType: "all",
    stylers: [{ visibility: "off" }],
  },
   {
    featureType: "landscape.natural",
    elementType: "geometry",
    stylers: [{ color: "#223322" }] // Use a dark green color
  },
  {
    featureType: "road",
    elementType: "geometry",
    stylers: [{ color: "#38414e" }],
  },
  {
    featureType: "road",
    elementType: "geometry.stroke",
    stylers: [{ color: "#212a37" }],
  },
  {
    featureType: "road",
    elementType: "labels.text.fill",
    stylers: [{ color: "#9ca5b3" }],
  },
  {
    featureType: "road.highway",
    elementType: "geometry",
    stylers: [{ color: "#746855" }],
  },
  {
    featureType: "road.highway",
    elementType: "geometry.stroke",
    stylers: [{ color: "#1f2835" }],
  },
  {
    featureType: "road.highway",
    elementType: "labels.text.fill",
    stylers: [{ color: "#f3d19c" }],
  },
  {
    featureType: "transit",
    elementType: "geometry",
    stylers: [{ color: "#2f3948" }],
  },
  {
    featureType: "transit.station",
    elementType: "labels.text.fill",
    stylers: [{ color: "#d59563" }],
  },
  {
    featureType: "water",
    elementType: "geometry",
    stylers: [{ color: "#17263c" }],
  },
  {
    featureType: "water",
    elementType: "labels.text.fill",
    stylers: [{ color: "#515c6d" }],
  },
  {
    featureType: "water",
    elementType: "labels.text.stroke",
    stylers: [{ color: "#17263c" }],
  },
];


const center = { lat: 37.721941, lng: -122.127001 };

const FIRE_LAYER_COLORS = ['#FFC107', '#FF3B30'];
const DEFAULT_VEHICLE_LENGTH_METRES = 8;
const MIN_LINK_STORAGE_CAPACITY_METRES = 18;
const UNKNOWN_LINK_QUEUE_COLOR = '#94A3B8';


// Coordinates for blocked roads and contraflow
const blockedRoadPositions = [
    { lat: 37.902543, lng: -122.266140 },
    { lat: 37.899952, lng: -122.261140 },
    { lat: 37.896049, lng: -122.259348 },
  ];


const contraflowPosition = { lat: 37.892949, lng: -122.268037 };
function MapComponent({state, updateState}) {
    const { contraflow, blockedRoads, time, dynamic_data } = state; // Destructure the state object  
  const GOOGLE_MAPS_API_KEY = 'AIzaSyASU8wb6swDIo3dNxkyjfraWijCImc3-OU';
  const [polylines, setPolylines] = useState([]);
  const [dynamicPolylines, setDynamicPolylines] = useState([]);
  const [dynamicNodes, setDynamicNodes] = useState([]);
  const [firePolygons, setFirePolygons] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  // const [time, setTime] = useState(1300); // Default time is 1300
  const [simulations, setSimulations] = useState([]); // State to hold simulation list
  const [selectedSimulation, setSelectedSimulation] = useState({'id':'simulation_1', 'name': '', 'total_time':3400, 'timeInterval': 100}); // Default to Baseline
  const [contraflowCenters, setContraflowCenters] = useState([]);
  const [linkMap, setLinkMap] = useState({});
  const [nodeMap, setNodeMap] = useState({});
  const [showPolyLines, setShowPolyLines] = useState(true); // Toggle state
  const [showAllNodes, setShowAllNodes] = useState(false);  
  const [showAllLinks, setShowAllLinks] = useState(false);
  const [selectedLinkIds, setSelectedLinkIds] = useState(new Set());
  const [selectedNodeIds, setSelectedNodeIds] = useState(new Set());
  const [showPercentiles, setShowPercentiles] = useState(false);
  const [showVariables, setShowVariables] = useState(false);
  const [variablesConfig, setVariablesConfig] = useState(null);
  const [variablesError, setVariablesError] = useState('');
  const [variablesLoading, setVariablesLoading] = useState(false);

  const [availableMetrics, setAvailableMetrics] = useState([]); // e.g., ["baseline_total", "lcr_total", ...]
  const [selectedMetric, setSelectedMetric] = useState(null);   // null → use top-level Value

  const mapRef = useRef(null); // Ref to access the map instance
  const linkMapRef = useRef({});
  const polylinesRef = useRef([]); // Ref to keep track of Polyline instances
  const staticPolylinesDataRef = useRef([]);
  const dynamicPolylinesRef = useRef([]) //Ref for dynamic polyline instances
  const dynamicNodesRef = useRef([]) 
  const vehicleDotsRef = useRef([]);
  const firePolygonsRef = useRef([]);

  const [selectorDialog, setSelectorDialog] = useState(null); // 'links' | 'nodes' | null
  const [selectorText, setSelectorText]   = useState('');

  const [linkScale, setLinkScale] = useState(null);   // { ranges:[{color,start,end}], min, max }
  const [nodeScale, setNodeScale] = useState(null);


  const openSelector = (type) => {
    setSelectorDialog(type);     // 'links' or 'nodes'
    setSelectorText('');
  };
  
  const closeSelector = () => {
    setSelectorDialog(null);
    setSelectorText('');
  };
  
  const parseIdList = (txt) =>
    (txt || '')
      .split(/[,\s]+/)
      .map(s => s.trim())
      .filter(Boolean);
  
  const submitSelector = () => {
    const ids = parseIdList(selectorText);
    if (!ids.length) { closeSelector(); return; }
  
    if (selectorDialog === 'links') {
      ids.forEach(id => handlePolylineClick(id));
    } else if (selectorDialog === 'nodes') {
      ids.forEach(id => handleNodeClick(id));
    }
    closeSelector();
  };

  function computeScales(data, type) {
    const items   = data.filter(d => d.Type === type);
    if (!items.length) return null;
  
    // group by colour → record the highest value per colour
    const colourMax = {};
    items.forEach(({ Color, Value }) => {
      const v = Number(Value);
      colourMax[Color] = Math.max(colourMax[Color] ?? -Infinity, v);
    });
  
    // sort colours by their “max” so low‑value colours come first
    const sorted = Object.entries(colourMax)
      .sort((a, b) => a[1] - b[1]);          // [ [colour, max], … ]
  
    const min  = Math.min(...items.map(i => +i.Value));
    const max  = Math.max(...items.map(i => +i.Value));
  
    let start  = min;
    const ranges = sorted.map(([color, colMax], i) => {
      const r = { color, start, end: colMax };
      start   = colMax;                      // next segment starts where this ends
      if (i === sorted.length - 1) r.end = max;
      return r;
    });
  
    return { ranges, min, max };
  }

  function colourForValue(v, scale) {
    if (!scale) return '#FFFFFF';            // fallback; shouldn't normally hit
    return scale.ranges.find(r => v >= r.start && v <= r.end)?.color
           ?? scale.ranges[scale.ranges.length - 1].color;
  }

  function getMetricValue(item) {
    if (!selectedMetric) return Number(item.Value);
    const src = item?.AllValues || {};
    const raw = src[selectedMetric];
    const num = raw === null || raw === undefined ? NaN : Number(raw);
    return isNaN(num) ? NaN : num;
  }
  
  function computeQuantiles(values) {
    const xs = values.filter((v) => typeof v === 'number' && !isNaN(v)).slice().sort((a, b) => a - b);
    if (!xs.length) return { min: 0, q50: 0, q75: 0, max: 0 };
    const qAt = (p) => {
      if (!xs.length) return 0;
      const idx = (xs.length - 1) * p;
      const lo = Math.floor(idx), hi = Math.ceil(idx);
      if (lo === hi) return xs[lo];
      return xs[lo] + (xs[hi] - xs[lo]) * (idx - lo);
    };
    return { min: xs[0], q50: qAt(0.5), q75: qAt(0.75), max: xs[xs.length - 1] };
  }
  
  // 3-band scale from the chosen metric (green/yellow/red)
  function computeScalesFromMetric(data, type) {
    const vals = (data || [])
      .filter((d) => d.Type === type)
      .map((d) => getMetricValue(d))
      .filter((v) => !isNaN(v));
    if (!vals.length) return null;
    const { min, q50, q75, max } = computeQuantiles(vals);
    const ranges = [
      { color: '#00FF00', start: min, end: q50 }, // bottom 50%
      { color: '#FFFF00', start: q50, end: q75 }, // 50–75%
      { color: '#FF0000', start: q75, end: max }, // top 25%
    ];
    return { ranges, min, max };
  }
  
  function rebuildScales() {
    if (!dynamic_data || !dynamic_data.length) return;
    if (selectedMetric) {
      setLinkScale(computeScalesFromMetric(dynamic_data, 'link'));
      setNodeScale(computeScalesFromMetric(dynamic_data, 'node'));
    } else {
      setLinkScale(computeScales(dynamic_data, 'link'));
      setNodeScale(computeScales(dynamic_data, 'node'));
    }
  }


  const togglePolylines = () => {

    if(showPolyLines)  //Toggled from true to false
    {
      //Set polyLine as empty
      clearAndSetPolylines([])
      updateDynamicLinkData();
    }
    else{
      clearAndSetDynamicPolylines([])
      clearAndSetDynamicNodes([])
    }
    setShowPolyLines((prev) => !prev);
  };

  const clearAndSetPolylines = (parsedPolylines) => {

    const map = mapRef.current; 
    staticPolylinesDataRef.current = parsedPolylines;

    console.log("Normal polylines: ", parsedPolylines)

    // Step 1: Remove all existing polylines from the map
    polylinesRef.current.forEach((polylineInstance) => {
      if (polylineInstance) {
        polylineInstance.setMap(null); // Remove polyline from the map
      }
    });
  
    // Step 2: Clear the current polyline references
    polylinesRef.current = [];

    // console.log(parsedPolylines)

    // Step 3: Add new polylines to the map
    parsedPolylines.forEach((polylineData) => {
      const polyline = new window.google.maps.Polyline({
        path: polylineData.path, // Array of LatLng objects
        strokeColor: getColorBasedOnQueue(polylineData.queue_vehicle_count, polylineData.link_id),
        strokeOpacity: 0.75,
        strokeWeight: selectedLinkIds.has(polylineData.link_id) ? 11 : 4,
        key: 'link-'+polylineData.link_id
      });

      // Add onClick listener to each polyline
      polyline.addListener("click", () => {
        // Handle click event for polyline (programmatically or interactively)
        console.log(`Polyline with linkId ${polylineData.link_id} clicked`);

        // Example: Add the polyline's ID to the selection set
        handlePolylineClick(polylineData.link_id); // You can call your `handlePolylineClick` function here
      });
  
      // Set the polyline on the map
      polyline.setMap(map);
  
      // Add the new polyline instance to the ref
      polylinesRef.current.push(polyline);
    });
  
    console.log("Removed old polylines and added new ones");
  };

  const handlePolylineClick = (linkId) => {

    console.log("Clicked on Link: ", linkId)
    let newSelectedLinkIds = selectedLinkIds;
    if (newSelectedLinkIds.has(linkId)) {
      newSelectedLinkIds.delete(linkId); // Deselect the polyline if it's already selected
    } else {
      newSelectedLinkIds.add(linkId); // Select the polyline
    }
    console.log("New selected links", newSelectedLinkIds)
    setSelectedLinkIdsWrapper(newSelectedLinkIds);
  };

  const handleNodeClick = (nodeId) => {

    console.log("Clicked on Node: ", nodeId)
    let newSelectedNodeIds = selectedNodeIds;
    if (newSelectedNodeIds.has(nodeId)) {
      newSelectedNodeIds.delete(nodeId); // Deselect the polyline if it's already selected
    } else {
      newSelectedNodeIds.add(nodeId); // Select the polyline
    }
    console.log("New selected links", newSelectedNodeIds)
    setSelectedNodeIdsWrapper(newSelectedNodeIds);
  };

  const removeSelectedLink = (linkId) => {
    handlePolylineClick(linkId);
  };
  
  const removeSelectedNode = (nodeId) => {
    handleNodeClick(nodeId)
  };


  const clearAndSetDynamicNodes = (parsedNodes) => {

    const map = mapRef.current; 

    // Step 1: Remove all existing polylines from the map
    dynamicNodesRef.current.forEach((nodeInstance) => {
      if (nodeInstance) {
        nodeInstance.setMap(null); // Remove polyline from the map
      }
    });
  
    // Step 2: Clear the current polyline references
    dynamicNodesRef.current = [];

    // console.log(parsedPolylines)  

    console.log("Here111: ", selectedNodeIds)
 
    // Step 3: Add new polylines to the map
    parsedNodes.map((nodeData) => {
      const circle = new window.google.maps.Circle({
          key: 'node-'+String(nodeData.id),
          center: { lat: nodeData.lat, lng: nodeData.lng },
          radius: selectedNodeIds.has(String(nodeData.id)) ? 36 : 16,
          fillColor: nodeData.color,
          fillOpacity: selectedNodeIds.has(String(nodeData.id))? 1: 0.7,
          strokeColor: "black",
          strokeOpacity: 1,
          strokeWeight: 2,
          map: map // Attach to the map
      });

      circle.addListener("click", () => {
        // Handle click event for polyline (programmatically or interactively)
        console.log(`Node with nodeId ${nodeData.id} clicked`);

        // Example: Add the polyline's ID to the selection set
        handleNodeClick(String(nodeData.id)); // You can call your `handlePolylineClick` function here
      });
       
      // Set the polyline on the map
      circle.setMap(map);
  
      // Add the new polyline instance to the ref
      dynamicNodesRef.current.push(circle);
    });
  
    console.log("Removed old dynamic nodes and added new ones");
  };


  const clearAndSetDynamicPolylines = (parsedPolylines) => {

    const map = mapRef.current; 

    // Step 1: Remove all existing polylines from the map
    dynamicPolylinesRef.current.forEach((polylineInstance) => {
      if (polylineInstance) {
        polylineInstance.setMap(null); // Remove polyline from the map
      }
    });
  
    // Step 2: Clear the current polyline references
    dynamicPolylinesRef.current = [];

    console.log("Dynamic polylines", parsedPolylines)

 
    // Step 3: Add new polylines to the map
    parsedPolylines.forEach((polylineData) => {
      const polyline = new window.google.maps.Polyline({
        path: polylineData.coordinates, // Array of LatLng objects
        strokeColor: polylineData.color, // Define color based on queue
        strokeOpacity: 0.75,
        strokeWeight: selectedLinkIds.has(String(polylineData.id)) ? 11 : 4,
        key: 'link-'+polylineData.id
      });

      polyline.addListener("click", () => {
        // Handle click event for polyline (programmatically or interactively)
        console.log(`Polyline with linkId ${polylineData.id} clicked`);

        // Example: Add the polyline's ID to the selection set
        handlePolylineClick(String(polylineData.id)); // You can call your `handlePolylineClick` function here
      });
  
      // Set the polyline on the map
      polyline.setMap(map);
  
      // Add the new polyline instance to the ref
      dynamicPolylinesRef.current.push(polyline);
    });
  
    console.log("Removed old dynamic polylines and added new ones");
  };


  // Fetch simulation data from Firebase Storage using Firebase SDK
  const fetchSimulationData = async (time, contraflowEnabled, simulationId) => {
    try {

      const filePath = `${simulationId}/link_stats/lbase-tester_at_${time}.csv`;
      const fileRef = ref(storage, filePath); // Get reference to the file
      
      // Get the download URL for the file
      const url = await getDownloadURL(fileRef);
      // console.log(url)

      // Fetch CSV content from the URL
      const response = await fetch(url);
      const csvText = await response.text();
    
      // Parse CSV and process data
      Papa.parse(csvText, {
        header: true,
        complete: (results) => {
          const data = results.data;
          // console.log(data)
          const parsedPolylines = data.map((row) => {
            if (row.geometry) {
              const path = row.geometry
                .replace('LINESTRING (', '')
                .replace(')', '')
                .split(', ')
                .map((point) => {
                  const [lng, lat] = point.split(' ').map(Number);
                  return { lat, lng };
                });
              return {
                path,
                queue_vehicle_count: parseInt(row.queue_vehicle_count, 10),
                link_id: row.link_id
              };
            }
            return null;
          }).filter(Boolean);
          console.log("polylines: ", parsedPolylines)
          clearAndSetPolylines(parsedPolylines)
          // setPolylines(parsedPolylines);
          setIsLoading(false);
        },
      });
    } catch (error) {
      console.error('Error fetching or parsing CSV:', error);
      setPolylines([]);
      setIsLoading(false);
    }
  };

  const updateDynamicLinkData = async () => {
    if (!dynamic_data || !dynamic_data.length) return;
  
    const newPolylines = [];
    const newNodes     = [];
  
    dynamic_data.forEach((item) => {
      const { id, Type } = item;
      const normId = String(parseFloat(id, 10));
  
      const v = getMetricValue(item);                  // number or NaN
      if (selectedMetric && (isNaN(v))) return;        // hide when metric is null/missing
  
      const color = colourForValue(
        selectedMetric ? v : Number(item.Value),
        Type === 'link' ? linkScale : nodeScale
      );
  
      if (Type === 'link') {
        const linkInfo = linkMap[normId];
        const geom = linkInfo?.geometry;
        if (geom) newPolylines.push({ id: normId, coordinates: parseLineString(geom), color });
      } else if (Type === 'node') {
        const nodeInfo = nodeMap[normId];
        if (nodeInfo?.lat && nodeInfo?.lng) {
          newNodes.push({ id: normId, lat: nodeInfo.lat, lng: nodeInfo.lng, color });
        }
      }
    });
  
  
    clearAndSetDynamicPolylines(newPolylines);
    clearAndSetDynamicNodes(newNodes);
  }

  const clearAndSetFirePolygons = (polys) => {
    const map = mapRef.current;
  
    firePolygonsRef.current.forEach(p => p && p.setMap(null));
    firePolygonsRef.current = [];
  
    polys.forEach(p => {
      const polygon = new window.google.maps.Polygon({
        paths: p.path,
        fillColor: p.color,
        fillOpacity: 0.35,
        strokeColor: p.color,
        strokeOpacity: 0.8,
        strokeWeight: 2,
        map
      });
      firePolygonsRef.current.push(polygon);
    });
  
    setFirePolygons(polys);
  };


  // Function to parse LINESTRING into an array of [lat, lng] coordinates
  function parseLineString(lineString) {
    const coords = lineString
      .replace("LINESTRING (", "")
      .replace(")", "")
      .split(", ")
      .map((point) => {
        const [lng, lat] = point.split(" ").map(Number); // Convert to numbers
        return {lat, lng}; // Leaflet requires [lat, lng]
      });
    return coords;
  }


  const fetchNodeData = async (simulationId) => {
    try {
        const filePath = `${simulationId}/inputs/berkeley_nodes.csv`; // Adjust if stored in a folder
        const fileRef = ref(storage, filePath);
        const url = await getDownloadURL(fileRef);

        const response = await fetch(url);
        const csvText = await response.text();

        const nodeMapTemp = {}; // Temporary object to store nodes

        Papa.parse(csvText, {
            header: true, // First row is treated as headers
            skipEmptyLines: true,
            complete: (result) => {
                result.data.forEach((row) => {
                    if (row.node_id && row.lat && row.lon) {
                        nodeMapTemp[row.node_id] = {
                            lat: parseFloat(row.lat),
                            lng: parseFloat(row.lon),
                            type: row.type,
                            osmid: row.node_osmid
                        };
                    }
                });

                console.log("Parsed Node Map:", nodeMapTemp); // Debugging
                setNodeMap(nodeMapTemp); // Store in state
            }
        });

    } catch (error) {
        console.error("Error fetching node data:", error);
    }
};

  const fetchLinkData = async (simulationId) => {
    linkMapRef.current = {};
    try {
      const filePath = `${simulationId}/inputs/berkeley_links.csv`; // Adjust if stored in a folder
      const fileRef = ref(storage, filePath);
      const url = await getDownloadURL(fileRef);

      const response = await fetch(url);
      const csvText = await response.text();

      Papa.parse(csvText, {
        header: true,
        complete: (results) => {
          const data = results.data;
          const linkMapTemp = {};

          data.forEach((row) => {
            if (row.link_id && row.geometry) {
              const path = row.geometry
                .replace('LINESTRING (', '')
                .replace(')', '')
                .split(', ')
                .map((point) => {
                  const [lng, lat] = point.split(' ').map(Number);
                  return { lat, lng };
                });

              linkMapTemp[row.link_id] = { path, ...row };
            }
          });

          // console.log(linkMapTemp)
          linkMapRef.current = linkMapTemp;
          setLinkMap(linkMapTemp);

          if (staticPolylinesDataRef.current.length) {
            clearAndSetPolylines(staticPolylinesDataRef.current);
          }
        },
      });
    } catch (error) {
      console.error('Error fetching or parsing CSV:', error);
    }
  };
  

  // Fetch contraflow links data from Firebase Storage
const fetchContraflowLinks = async (simulationId) => {
    try {
      const filePath = `${simulationId}/inputs/contraflow_links.csv`; // Path to the contraflow links file
      const fileRef = ref(storage, filePath); // Get reference to the file
      
      // Get the download URL for the file
      const url = await getDownloadURL(fileRef);
  
      // Fetch CSV content from the URL
      const response = await fetch(url);
      const csvText = await response.text();
      
      // Parse CSV and log the data
      Papa.parse(csvText, {
        header: true,
        complete: (results) => {
          const contraflowLinks = results.data;
          console.log("Contraflow Links:", contraflowLinks); // Console log the parsed contraflow links

          // Calculate the center for each contraflow link
        const centers = contraflowLinks.map((link) => {
            if (link.geometry) {
              // Parse the geometry field to extract coordinates
              const coordinates = link.geometry
                .replace('LINESTRING (', '')
                .replace(')', '')
                .split(', ')
                .map((point) => {
                  const [lng, lat] = point.split(' ').map(Number);
                  return { lat, lng };
                });
              
              // Calculate the center by averaging the coordinates
              const center = coordinates.reduce((acc, curr) => {
                acc.lat += curr.lat;
                acc.lng += curr.lng;
                return acc;
              }, { lat: 0, lng: 0 });
  
              center.lat /= coordinates.length;
              center.lng /= coordinates.length;
              return center;
            }
            return null;
          }).filter(Boolean);

          // Deduplicate the centers array based on lat,lng
        const uniqueCenters = Array.from(
            new Set(centers.map(a => `${a.lat},${a.lng}`)) // Create a set of unique "lat,lng" strings
          ).map(e => {
            const [lat, lng] = e.split(','); // Split back into lat, lng
            return { lat: parseFloat(lat), lng: parseFloat(lng) }; // Return an object with lat and lng
          });

          console.log(uniqueCenters)

          setContraflowCenters(uniqueCenters)
        },
      });
    } catch (error) {
      console.error('Error fetching or parsing contraflow links CSV:', error);
      setContraflowCenters([])
    }
  };

  // Fetch simulations from Firestore
  const fetchSimulations = async () => {
    try {
      const simulationsCollection = collection(firestore, 'simulations');
      const simulationDocs = await getDocs(simulationsCollection);
      const simulationsList = simulationDocs.docs.map((doc) => {
        const data = doc.data() || {};
        return {
          id: doc.id,
          ...data,
          // normalize with fallbacks
          timeInterval: Number(data.timeInterval ?? 100),
          total_time: Number(data.total_time ?? 0),
        };
      });
      // console.log(simulationsList)
      const reorderedList = [
        simulationsList[0], // keep first item
        ...simulationsList.slice(1).reverse() // reverse everything after the first
      ];
      setSimulations(reorderedList);
    } catch (error) {
      console.error('Error fetching simulations:', error);
    }
  };

  const fetchFireData = async (t, simulationId) => {
    const filePath = `${simulationId}/fire_inputs/fire_${t}.geojson`;
    try {
      const url  = await getDownloadURL(ref(storage, filePath));
      const json = await (await fetch(url)).json();

      const features = json?.features ?? [];

      if (!features.length) {
        clearAndSetFirePolygons([]);          // no fire this timestep
        return;
      }

      const hazardIds = [...new Set(
        features.map((feature, featureIndex) => (
          feature?.properties?.hazard_id ?? feature?.id ?? `feature-${featureIndex}`
        ))
      )];
      const colorByHazardId = new Map(
        hazardIds.map((hazardId, hazardIndex) => [
          hazardId,
          FIRE_LAYER_COLORS[hazardIndex % FIRE_LAYER_COLORS.length],
        ])
      );
    
      const polys = features.flatMap((feature, featureIndex) => {
        const geometry = feature?.geometry || {};
        const type = String(geometry.type || "").toLowerCase();
        const hazardId = feature?.properties?.hazard_id ?? feature?.id ?? `feature-${featureIndex}`;
        const color = colorByHazardId.get(hazardId);
        const polygonCoordinates = type === "polygon"
          ? [geometry.coordinates]
          : type === "multipolygon"
            ? geometry.coordinates
            : [];

        return (Array.isArray(polygonCoordinates) ? polygonCoordinates : [])
          .map((polygon, polygonIndex) => {
            const outerRing = Array.isArray(polygon?.[0]) ? polygon[0] : [];
            const path = outerRing
              .filter((coordinate) => Array.isArray(coordinate) && coordinate.length >= 2)
              .map(([lng, lat]) => ({ lat: Number(lat), lng: Number(lng) }))
              .filter(({ lat, lng }) => Number.isFinite(lat) && Number.isFinite(lng));
            if (path.length < 3) return null;
            return {
              id: `fire-${featureIndex}-${polygonIndex}`,
              path,
              hazardId,
              color,
            };
          })
          .filter(Boolean);
      });
  
      clearAndSetFirePolygons(polys);
    } catch (error) {
      console.error("Error fetching or parsing fire GeoJSON:", error);
      clearAndSetFirePolygons([]);
    }
  };

  // Handle time change from the slider
  const handleTimeChange = (event) => {
    const newTime = Number(event.target.value); // Get the new time value
    // setTime(newTime); // Update the time state
    updateState({
        contraflow: state.contraflow,
        blockedRoads: state.blockedRoads,
        time: newTime, // Keep current time if not provided
        dynamic_data: state.dynamic_data
    })
    setIsLoading(true); // Set loading state to true while fetching data
    fetchSimulationData(newTime, contraflow, selectedSimulation.id); // Call fetchSimulationData when time changes
  };

  // Determine link color from the percentage of physical link storage occupied by queued vehicles.
  const getColorBasedOnQueue = (queueCount, linkId) => {
    const queuedVehicles = Number(queueCount);
    const link = linkMapRef.current[String(linkId)];
    const lengthMetres = Number(link?.length);
    const lanes = Number(link?.lanes);

    if (
      !Number.isFinite(queuedVehicles) ||
      !Number.isFinite(lengthMetres) ||
      !Number.isFinite(lanes) ||
      queuedVehicles < 0 ||
      lengthMetres <= 0 ||
      lanes <= 0
    ) {
      return UNKNOWN_LINK_QUEUE_COLOR;
    }

    const storageCapacity = Math.max(
      MIN_LINK_STORAGE_CAPACITY_METRES,
      lengthMetres * lanes
    );
    const queuedSpace = queuedVehicles * DEFAULT_VEHICLE_LENGTH_METRES;
    const queuedFraction = queuedSpace / storageCapacity;

    if (queuedFraction <= 0.25) return '#00FF00';
    if (queuedFraction < 0.5) return '#FFA500';
    return '#FF0000';
  };

  // Handle Simulation Change
  const handleSimulationChange = (nextSimulation) => {
    if (!nextSimulation) return;

    const step = getTimeStep(nextSimulation);
    const alignedTime = alignToStep(time, step);

    const next = {
      ...nextSimulation,
      timeInterval: step,
      total_time: Number(nextSimulation.total_time ?? 0),
    };

    setSelectedSimulation(next);
    setIsLoading(true);

    // keep app state in sync (including the snapped time)
    updateState({
      ...state,
      time: alignedTime,
      selectedSimulationId: next.id,
      maxTime: next.total_time
    });

    fetchSimulationData(time, contraflow, nextSimulation.id);
  };

  const handleSimulationNameSave = async (simulationId, nextName) => {
    await updateSimulationName(simulationId, nextName);
    setSimulations((current) => current.map((simulation) =>
      simulation.id === simulationId ? { ...simulation, name: nextName } : simulation
    ));
    setSelectedSimulation((current) =>
      current.id === simulationId ? { ...current, name: nextName } : current
    );
  };

  const openVariables = async () => {
    const simulationId = selectedSimulation?.id;
    setShowVariables(true);
    setVariablesConfig(null);
    setVariablesError('');

    if (!simulationId) {
      setVariablesError('No simulation is selected.');
      return;
    }

    setVariablesLoading(true);
    try {
      const fileRef = ref(storage, `${simulationId}/config/config.json`);
      const url = await getDownloadURL(fileRef);
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Could not download config.json (${response.status}).`);
      }
      const configText = await response.text();
      setVariablesConfig(JSON.parse(configText));
    } catch (error) {
      console.error('Error loading simulation config:', error);
      setVariablesError(error?.message || 'Could not load config.json.');
    } finally {
      setVariablesLoading(false);
    }
  };

  useEffect(() => {
    fetchContraflowLinks(selectedSimulation.id);
    fetchLinkData(selectedSimulation.id);
    fetchNodeData(selectedSimulation.id);
    updateState({
      maxTime: selectedSimulation.total_time,
      selectedSimulationId: selectedSimulation.id
    })
  }, [selectedSimulation])

  useEffect(() => {
    fetchSimulations();
    fetchLinkData(selectedSimulation.id);
    fetchNodeData(selectedSimulation.id);
  }, []);

  
  // 1) When dynamic_data refreshes: rebuild metric list and RESET selectedMetric to null
  useEffect(() => {
    if (!dynamic_data || !dynamic_data.length) return;

    const keys = Array.from(
      new Set(dynamic_data.flatMap((d) => Object.keys(d.AllValues || {})))
    );
    setAvailableMetrics(keys);
    setSelectedMetric(null); // reset on data refresh
  }, [dynamic_data]);

  // 2) Whenever data or the user-chosen metric changes: rebuild scales (and let your existing
  //    [linkScale, nodeScale] effect drive updateDynamicLinkData + any toggling)
  useEffect(() => {
    if (!dynamic_data || !dynamic_data.length) return;
    rebuildScales();
  }, [dynamic_data, selectedMetric]);

  useEffect(() => {
    if (linkScale || nodeScale) {
      updateDynamicLinkData();               // colour everything
      if (showPolyLines) togglePolylines();  // switch to AI view once
    }
  }, [linkScale, nodeScale]);

  // UseEffect to call fetchSimulationData initially on component load
  useEffect(() => {
    if(showPolyLines)
      fetchSimulationData(time, contraflow, selectedSimulation.id);
    fetchFireData(time, selectedSimulation.id);
  }, [contraflow, time, showPolyLines]); // Empty dependency array ensures it runs only once on component mount


    // useEffect to watch selectedLinkIds changes and update polyline colors
  const setSelectedLinkIdsWrapper = (newSelectedLinkIds) => {

    setSelectedLinkIds(newSelectedLinkIds)

    updateState({selectedLinkIds: newSelectedLinkIds})



    polylinesRef.current.forEach((polylineInstance) => {
      if (polylineInstance) {
        const polylineLinkId = polylineInstance.key.replace('link-', ''); // Get the link_id from the key

        // Update the color if the linkId is selected
        polylineInstance.setOptions({
          strokeWeight: selectedLinkIds.has(polylineLinkId) ? 11 : 4
        });
      }
    });

    dynamicPolylinesRef.current.forEach((polylineInstance) => {
      if (polylineInstance) {
        const polylineLinkId = polylineInstance.key.replace('link-', ''); // Get the link_id from the key

        // Update the color if the linkId is selected
        polylineInstance.setOptions({
          strokeWeight: selectedLinkIds.has(polylineLinkId) ? 11 : 4,
        });
      }
    });
  }; 

  const setSelectedNodeIdsWrapper = (newSelectedNodeIds) => {

    setSelectedNodeIds(newSelectedNodeIds)

    updateState({selectedNodeIds: newSelectedNodeIds})

    if(dynamicNodesRef){
      dynamicNodesRef.current.forEach((nodeInstance) => {
        if (nodeInstance) {
          const NodeId = nodeInstance.key.replace('node-', ''); // Get the link_id from the key         
  
          // Update the color if the linkId is selected
          nodeInstance.setOptions({
            radius: newSelectedNodeIds.has(NodeId) ? 36 : 16,
            fillOpacity: newSelectedNodeIds.has(NodeId) ? 1 : 0.7,
          });
        }
      });
    }

  }; 


  const handleBoundaryChange = (val, idx) => {
    setLinkScale((prev) => {
      if (!prev) return prev;
      const ranges = prev.ranges.map((r) => ({ ...r }));
      if (idx < 0 || idx >= ranges.length - 1) return prev;
      ranges[idx].end = val;
      ranges[idx + 1].start = val;
      return { ...prev, ranges };
    });
    updateDynamicLinkData();
  };

  const formatVariableValue = (value, { commaSeparated = false } = {}) => {
    if (Array.isArray(value)) return value.join(', ');
    if (value === null || value === undefined || value === '') return '—';
    if (commaSeparated && typeof value === 'object') return Object.values(value).flat().join(', ');
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  const departureMin = variablesConfig?.min_departure_time
    ?? variablesConfig?.departure_times?.min_departure_time;
  const departureMax = variablesConfig?.max_departure_time
    ?? variablesConfig?.departure_times?.max_departure_time;
  const departureTime = departureMin == null && departureMax == null
    ? null
    : `[${formatVariableValue(departureMin)}, ${formatVariableValue(departureMax)}] minutes`;

  const routingCode = String(
    variablesConfig?.routing_strat ?? variablesConfig?.routing_strategy ?? ''
  ).toLowerCase();
  const routingLabel = routingCode === 'k1' || routingCode === 'k2' || routingCode === 'k5'
    ? `shortest paths (${routingCode})`
    : 'rerouting';

  const activeHousesRaw = variablesConfig?.active_houses_percentage;
  const activeHousesRatio = Number(activeHousesRaw);
  const activeHousesPercentage = activeHousesRaw !== null
    && activeHousesRaw !== undefined
    && activeHousesRaw !== ''
    && Number.isFinite(activeHousesRatio)
    ? `${Math.round(activeHousesRatio * 10000) / 100}%`
    : null;

  const variablesRows = variablesConfig ? [
    ['vehicles_per_household', variablesConfig.vehicles_per_household],
    ['departure_time', departureTime],
    ['destinations', variablesConfig.destinations, { commaSeparated: true }],
    ['routing_strat', routingLabel],
    ['active_houses_percentage', activeHousesPercentage],
    ['time_of_day', variablesConfig.time_of_day],
    [
      'background_traffic',
      variablesConfig.bg_traffic ?? variablesConfig.background_traffic
    ],
  ] : [];

  
  return (
      <div
        style={{
          position: 'relative',
          height: '100%',
          backgroundColor: '#2C2C2C', // A dark grey background
          color: '#CCC',              // Softer text color
          borderRadius: '8px',
          border: '1px solid #444'    // Subtle border
        }}
      >

        <div style={{padding: '10px'}}>
          {/* Controls container relative to the map view */}
          <div
            style={{
              position: 'absolute',
              top: '10px',
              left: 0,
              width: '100%',
              zIndex: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '0 10px'
            }}
          >
            {/* Toggle button on the left */}
            <button
              onClick={togglePolylines}
              style={{
                padding: '5px 10px',
                fontSize: '14px',
                cursor: 'pointer',
                borderRadius: '4px',
                backgroundColor: '#3A3A3A',
                color: '#FFF',
                border: 'none'
              }}
            >
              {showPolyLines ? 'Show AI results' : 'Back'}
            </button>
        
            <div style={{ marginRight: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <SimulationNameEditor
                simulation={selectedSimulation}
                onSave={handleSimulationNameSave}
              />
              <SimulationSelector
                simulations={simulations}
                selectedId={selectedSimulation?.id}
                onSelect={handleSimulationChange}
              />
            </div>
          </div>
        
          {/* Time Slider container with padding-top so it won't be overlapped */}
          <div style={{ paddingTop: '60px' }}>
            <input
              type="range"
              min="0"
              max={selectedSimulation.total_time}
              step={getTimeStep(selectedSimulation)} 
              value={time}
              onChange={handleTimeChange}
              style={{
                width: '100%',
                marginBottom: '10px',
                backgroundColor: '#3A3A3A'
              }}
            />
            <div style={{ textAlign: 'center' }}>{formatTime(time)}</div>
          </div>
        
          {showPolyLines && (
             <div style={{ marginBottom: '20px', display:'flex', alignItems:'center' }}>
                {/* NEW checkbox – show every node in blue */}
                <input
                  type="checkbox"
                  checked={showAllNodes}
                  onChange={() => setShowAllNodes(prev => !prev)}
                  style={{ marginRight: '5px' }}
                />
                <label style={{ color: '#FFF' }}>Show all nodes</label>

                <input
                  type="checkbox"
                  checked={showAllLinks}
                  onChange={() => setShowAllLinks(prev => !prev)}
                  style={{ marginLeft:'15px', marginRight:'5px' }}
                />
                <label style={{ color:'#FFF' }}>Show all links</label>
            </div>
          )}
        
          <div>
            {state.selectedLinkIds.size > 0 && (
              <div style={{ marginBottom: '10px' }}>
                <p style={{ margin: '0 0 5px 0' }}>Selected roads</p>
                <div>
                  {Array.from(state.selectedLinkIds).map((id) => (
                    <span
                      key={id}
                      style={{
                        padding: '4px 8px',
                        background: '#444',
                        borderRadius: '4px',
                        marginRight: '5px',
                        display: 'inline-block',
                        color: '#FFF'
                      }}
                    >
                      {id}
                      <button
                        onClick={() => removeSelectedLink(id)}
                        style={{
                          marginLeft: '8px',
                          background: 'transparent',
                          border: 'none',
                          color: '#FF6B6B',
                          cursor: 'pointer',
                          fontSize: '14px',
                          padding: '0',
                          lineHeight: '1'
                        }}
                        title="Remove selection"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
            {state.selectedNodeIds.size > 0 && (
              <div>
                <p style={{ margin: '0 0 5px 0' }}>Selected nodes</p>
                <div>
                  {Array.from(state.selectedNodeIds).map((id) => (
                    <span
                      key={id}
                      style={{
                        padding: '4px 8px',
                        background: '#444',
                        borderRadius: '4px',
                        marginRight: '5px',
                        display: 'inline-block',
                        color: '#FFF'
                      }}
                    >
                      {id}
                      <button
                        onClick={() => removeSelectedNode(id)}
                        style={{
                          marginLeft: '8px',
                          background: 'transparent',
                          border: 'none',
                          color: '#FF6B6B',
                          cursor: 'pointer',
                          fontSize: '14px',
                          padding: '0',
                          lineHeight: '1'
                        }}
                        title="Remove selection"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div style={{ display:'flex', gap: '8px', margin: '20px 0 20px' }}>
            <button
              onClick={() => openSelector('links')}
              style={{
                padding: '5px 10px',
                fontSize: '14px',
                cursor: 'pointer',
                borderRadius: '4px',
                backgroundColor: '#3A3A3A',
                color: '#FFF',
                border: 'none'
              }}
            >
              Select links
            </button>
            <button
              onClick={() => openSelector('nodes')}
              style={{
                padding: '5px 10px',
                fontSize: '14px',
                cursor: 'pointer',
                borderRadius: '4px',
                backgroundColor: '#3A3A3A',
                color: '#FFF',
                border: 'none'
              }}
            >
              Select nodes
            </button>

            {Array.isArray(selectedSimulation?.evacuation_percentiles) &&
            selectedSimulation.evacuation_percentiles.length > 0 && (
              <button
                onClick={() => setShowPercentiles(true)}
                style={{
                  padding: '5px 10px',
                  fontSize: '14px',
                  cursor: 'pointer',
                  borderRadius: '4px',
                  backgroundColor: '#3A3A3A',
                  color: '#FFF',
                  border: 'none'
                }}
              >
                View percentile
              </button>
            )}

            <button
              onClick={openVariables}
              style={{
                padding: '5px 10px',
                fontSize: '14px',
                cursor: 'pointer',
                borderRadius: '4px',
                backgroundColor: '#3A3A3A',
                color: '#FFF',
                border: 'none'
              }}
            >
              Variables
            </button>
            
          </div>

          

          {selectorDialog && (
            <div
              style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 9999
              }}
              onClick={closeSelector}
            >
              <div
                style={{
                  width: 'min(520px, 90vw)',
                  background: '#2f2f2f',
                  color: '#fff',
                  border: '1px solid #444',
                  borderRadius: 8,
                  padding: 16,
                  boxShadow: '0 10px 30px rgba(0,0,0,0.4)'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div style={{ fontWeight: 600, marginBottom: 8 }}>
                  {selectorDialog === 'links' ? 'Select links' : 'Select nodes'}
                </div>
                <div style={{ fontSize: 14, opacity: 0.8, marginBottom: 8 }}>
                  Enter one or more IDs separated by commas or spaces.
                </div>
                <input
                  autoFocus
                  value={selectorText}
                  onChange={(e) => setSelectorText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') submitSelector();
                    if (e.key === 'Escape') closeSelector();
                  }}
                  placeholder={selectorDialog === 'links' ? 'e.g. 101, 205 207' : 'e.g. 43, 151 171'}
                  style={{
                    width: '95%',
                    padding: '8px 10px',
                    borderRadius: 4,
                    border: '1px solid #444',
                    background: '#1f1f1f',
                    color: '#fff',
                    marginBottom: 12
                  }}
                />
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button
                    onClick={closeSelector}
                    style={{
                      padding: '5px 10px',
                      fontSize: '14px',
                      cursor: 'pointer',
                      borderRadius: '4px',
                      backgroundColor: '#3A3A3A',
                      color: '#FFF',
                      border: 'none'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={submitSelector}
                    style={{
                      padding: '5px 10px',
                      fontSize: '14px',
                      cursor: 'pointer',
                      borderRadius: '4px',
                      backgroundColor: '#3A3A3A',
                      color: '#FFF',
                      border: 'none'
                    }}
                  >
                    Select
                  </button>
                </div>
              </div>
            </div>
          )}

          {showPercentiles && (
            <div
              style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 9999
              }}
              onClick={() => setShowPercentiles(false)}
            >
              <div
                style={{
                  width: 'min(520px, 90vw)',
                  background: '#2f2f2f',
                  color: '#fff',
                  border: '1px solid #444',
                  borderRadius: 8,
                  padding: 16,
                  boxShadow: '0 10px 30px rgba(0,0,0,0.4)'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div style={{ fontWeight: 600, marginBottom: 8 }}>
                  Evacuation percentiles
                </div>
                <div style={{ fontSize: 14, opacity: 0.8, marginBottom: 12 }}>
                  Each row shows the time when that share of people has evacuated.
                </div>

                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #444' }}>
                        Percentile
                      </th>
                      <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #444' }}>
                        Evacuation time
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedSimulation.evacuation_percentiles.map((t, idx) => (
                      <tr key={idx}>
                        <td style={{ padding: '6px 8px', borderBottom: '1px solid #333' }}>
                          {idx * 10}%
                        </td>
                        <td style={{ padding: '6px 8px', borderBottom: '1px solid #333' }}>
                          {formatTime(Number(t) || 0)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
                  <button
                    onClick={() => setShowPercentiles(false)}
                    style={{
                      padding: '5px 10px',
                      fontSize: '14px',
                      cursor: 'pointer',
                      borderRadius: '4px',
                      backgroundColor: '#3A3A3A',
                      color: '#FFF',
                      border: 'none'
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}

          {showVariables && (
            <div
              style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 9999
              }}
              onClick={() => setShowVariables(false)}
            >
              <div
                style={{
                  width: 'min(760px, 90vw)',
                  maxHeight: '80vh',
                  display: 'flex',
                  flexDirection: 'column',
                  background: '#2f2f2f',
                  color: '#fff',
                  border: '1px solid #444',
                  borderRadius: 8,
                  padding: 16,
                  boxShadow: '0 10px 30px rgba(0,0,0,0.4)'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div style={{ fontWeight: 600, marginBottom: 12 }}>
                  Variables — {selectedSimulation?.name || selectedSimulation?.id}
                </div>

                {variablesLoading && <div>Loading config.json…</div>}
                {variablesError && <div style={{ color: '#ff8a8a' }}>{variablesError}</div>}
                {!variablesLoading && !variablesError && variablesConfig && (
                  <div
                    style={{
                      border: '1px solid #334155',
                      borderRadius: 12,
                      overflow: 'auto'
                    }}
                  >
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <th
                            style={{
                              width: '42%',
                              padding: '12px 14px',
                              textAlign: 'left',
                              background: '#20293a',
                              color: '#94a3b8',
                              fontSize: 14,
                              fontWeight: 700,
                              borderBottom: '1px solid #293548'
                            }}
                          >
                            Variable
                          </th>
                          <th
                            style={{
                              padding: '12px 14px',
                              textAlign: 'left',
                              background: '#20293a',
                              color: '#94a3b8',
                              fontSize: 14,
                              fontWeight: 700,
                              borderBottom: '1px solid #293548'
                            }}
                          >
                            Value
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {variablesRows.map(([label, value, options], index) => (
                          <tr key={label}>
                            <td
                              style={{
                                padding: '12px 14px',
                                color: '#cbd5e1',
                                fontSize: 14,
                                fontWeight: 600,
                                borderBottom: index < variablesRows.length - 1
                                  ? '1px solid #293548'
                                  : 'none'
                              }}
                            >
                              {label}
                            </td>
                            <td
                              style={{
                                padding: '12px 14px',
                                color: '#e5e7eb',
                                fontSize: 14,
                                borderBottom: index < variablesRows.length - 1
                                  ? '1px solid #293548'
                                  : 'none',
                                overflowWrap: 'anywhere'
                              }}
                            >
                              {formatVariableValue(value, options)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
                  <button
                    onClick={() => setShowVariables(false)}
                    style={{
                      padding: '5px 10px',
                      fontSize: '14px',
                      cursor: 'pointer',
                      borderRadius: '4px',
                      backgroundColor: '#3A3A3A',
                      color: '#FFF',
                      border: 'none'
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
        
          {state?.mapDataText && !(linkScale || nodeScale) &&  !showPolyLines && (
            <div style={{ padding: '1px' }}>
              <p style={{ color: '#FFF' }}>{state.mapDataText}</p>
            </div>
          )}

          {showPolyLines && (
            <div style={{ padding: '1px' }}>
              <p style={{ color: '#FFF', whiteSpace: 'pre-line' }}>{state.generalMapLegendText}</p>
            </div>
          )}

          {/* --- Metric selector (built from AllValues) --- */}
          {!showPolyLines && availableMetrics.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, margin: '8px 0 12px' }}>
              <span style={{ alignSelf: 'center', opacity: 0.8 }}>Metric:</span>
              <button
                onClick={() => setSelectedMetric(null)}
                style={{
                  padding: '5px 10px',
                  fontSize: '14px',
                  cursor: 'pointer',
                  borderRadius: '4px',
                  backgroundColor: selectedMetric === null ? '#5a5a5a' : '#3A3A3A',
                  color: '#FFF',
                  border: 'none',
                }}
              >
                Default
              </button>
              {availableMetrics.map((m) => (
                <button
                  key={m}
                  onClick={() => setSelectedMetric(m)}
                  style={{
                    padding: '5px 10px',
                    fontSize: '14px',
                    cursor: 'pointer',
                    borderRadius: '4px',
                    backgroundColor: selectedMetric === m ? '#5a5a5a' : '#3A3A3A',
                    color: '#FFF',
                    border: 'none',
                  }}
                >
                  {m}
                </button>
              ))}
            </div>
          )}

          {/* ─── colour scale for LINKS ─── */}
          {!showPolyLines && linkScale && (
            <ColourBar
              scale={linkScale}
              onBoundaryChange={handleBoundaryChange}
            />
          )}

          {/* ─── colour scale for NODES ─── */}
          {!showPolyLines && nodeScale && (
            <ColourBar
              scale={nodeScale}
              onBoundaryChange={(val, idx) => {
                setNodeScale((s) => {
                  if (!s) return s;
                  const r = [...s.ranges];
                  if (idx === 0) { r[0].end = val; r[1].start = val; }
                  if (idx === 1) { r[1].end = val; r[2].start = val; }
                  return { ...s, ranges: r };
                });
                updateDynamicLinkData();
              }}
            />
          )}

      </div>  
  
      <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY}>
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={center}
          zoom={15}
          onLoad={(map) => { mapRef.current = map; }} // Store the map instance
          options={{ styles: darkMapStyle }}
        >
          {isLoading && <div style={{ position: 'absolute', top: '50%', left: '50%' }}>Loading...</div>}
  
          {showAllNodes && Object.entries(nodeMap).map(([id, n]) => (
            <Circle
              key={`all-${id}`}
              center={{ lat: n.lat, lng: n.lng }}
              radius={selectedNodeIds.has(String(id)) ? 36 : 16}
              options={{
                fillColor: selectedNodeIds.has(String(id))? '#BFFFBF': '#1E90FF',          // bright blue
                fillOpacity: selectedNodeIds.has(String(id))? 1: 0.7,
                strokeColor: 'black',
                strokeOpacity: 1,
                strokeWeight: 1,
              }}
              onClick={() => handleNodeClick(String(id))} 
            />
          ))}

          {showAllLinks && Object.entries(linkMap).map(([id, l]) => (
            <Polyline
              key={`all-${id}`}
              path={l.path}
              options={{
                strokeColor: selectedLinkIds.has(id)? '#BFFFBF': '#00BFFF',       // light blue
                strokeOpacity: selectedLinkIds.has(id)? 1: 0.6,
                strokeWeight: selectedLinkIds.has(id)? 9 : 4,
              }}
              onClick={() => handlePolylineClick(id)}
            />
          ))}
  
          {/* Render Static Polylines if toggled ON */}
          {showPolyLines && polylines.map((polyline, index) => (
            <Polyline
              key={polyline.link_id}
              path={polyline.path}
              options={{
                strokeColor: getColorBasedOnQueue(polyline.queue_vehicle_count, polyline.link_id),
                strokeOpacity: 0.75,
                strokeWeight: selectedLinkIds.has(polyline.link_id) ? 11 : 4,
              }}
              onClick={() => handlePolylineClick(polyline.link_id)}
              onLoad={(polylineInstance) => { polylinesRef.current.push(polylineInstance); }}
            />
          ))}
  
          {/* Render dynamic polylines */}
          {!showPolyLines && dynamicPolylines.map((polyline, index) => (
            <Polyline
              key={index}
              path={polyline.coordinates}
              options={{
                strokeColor: polyline.color,
                strokeOpacity: 0.75,
                strokeWeight: selectedLinkIds.has(polyline.id) ? 11 : 4,
              }}
              onClick={() => handlePolylineClick(polyline.id)}
              onLoad={(polylineInstance) => { dynamicPolylinesRef.current.push(polylineInstance); }}
            />
          ))}
  
          {/* Render Nodes */}
          {!showPolyLines && dynamicNodes.map((node, index) => (
            <Circle
              key={index}
              center={{ lat: node.lat, lng: node.lng }}
              radius={selectedNodeIds.has(String(node.id)) ? 36 : 16}
              options={{
                fillColor: node.color,
                fillOpacity: selectedNodeIds.has(String(node.id)) ? 1 : 0.7,
                strokeColor: "black",
                strokeOpacity: 1,
                strokeWeight: 2,
              }}
              onClick={() => handleNodeClick(node.id)}
              onLoad={(nodeInstance) => { dynamicNodesRef.current.push(nodeInstance); }}
            />
          ))}

          {firePolygons.map(fp => (
            <Polygon
              key={fp.id}
              paths={fp.path}
              options={{
                fillColor: fp.color,
                fillOpacity: 0.35,
                strokeColor: fp.color,
                strokeOpacity: 0.8,
                strokeWeight: 2
              }}
              onLoad={poly => firePolygonsRef.current.push(poly)}
            />
          ))}
  
          {/* Render blocked road icons */}
          {blockedRoadPositions.slice(0, blockedRoads).map((position, index) => (
            <Marker
              key={`blocked-road-${index}`}
              position={position}
              icon={{
                url: BlockedRoadIcon,
                scaledSize: new window.google.maps.Size(40, 40),
                anchor: new window.google.maps.Point(20, 20),
              }}
            />
          ))}
  
          {/* Render contraflow icons */}
          {contraflowCenters.map((center, index) => (
            <Marker
              key={`contraflow-${index}`}
              position={center}
              icon={{
                url: ContraflowIcon,
                scaledSize: new window.google.maps.Size(20, 20),
                anchor: new window.google.maps.Point(10, 10),
              }}
            />
          ))}
  
          {/* Render contraflow icon if enabled */}
          {contraflow && (
            <Marker
              position={contraflowPosition}
              icon={{
                url: ContraflowIcon,
                scaledSize: new window.google.maps.Size(40, 40),
                anchor: new window.google.maps.Point(20, 20),
              }}
            />
          )}
        </GoogleMap>
      </LoadScript>
    </div>
  );
}

export default MapComponent;
